import { Resources } from 'constants/resources';

import { AxiosResponse, AxiosError } from 'axios';
import { axios } from 'config/axios';
import qs from 'qs';
import { GatewayConversionSetting } from 'types/embedded/gatewayConversionSetting';
import {
  ConversionItem,
  TotalOrders,
  GatewayConversionListItem,
  OrderConversionChartData,
} from 'types/embedded/orderConversion';

export type OrderConversionRequestParams = {
  period: string;
  detailed?: boolean;
  merchantId: string | null | undefined;
  providerIds?: string[];
  gatewayIds?: string[];
  direction: string | null;
};

export type OrderConversionChartRequestParams = {
  period: string | null;
  timezone?: string | null;
  merchantId: string | null;
  providerIds: string[];
};

export type OrderConversionResponse = {
  summaryConversionItems: ConversionItem[];
  totalOrders: TotalOrders;
  gatewayConversionList: GatewayConversionListItem[];
};

export const getOrderConversion = async (
  filter: OrderConversionRequestParams,
): Promise<OrderConversionResponse> => {
  const params = qs.stringify(
    {
      ...filter,
    },
    { arrayFormat: 'indices', encode: true, allowDots: true },
  );
  return axios
    .get(`${Resources.OrderConversion}?${params}`)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const getGatewayConversionSettings = async (
  id: number,
): Promise<GatewayConversionSetting> => {
  return axios
    .get(`gateway_conversion_settings/${id}`)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export type CreateGatewayConversionSettingsParams = {
  merchant: string;
  gateway: string;
  successDeviation: string;
  cancelDeviation: string;
  pendingDeviation: string;
  minOrdersCount: number;
  notify: boolean;
};

export const createGatewayConversionSettings = async (
  data: CreateGatewayConversionSettingsParams,
): Promise<AxiosResponse<GatewayConversionSetting>> => {
  return axios
    .post('gateway_conversion_settings', data)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError<{ detail: string }>) => Promise.reject(error));
};

export type UpdateGatewayConversionSettingsParams = {
  successDeviation: string;
  cancelDeviation: string;
  pendingDeviation: string;
  minOrdersCount: number;
  notify: boolean;
};

export const updateGatewayConversionSettings = async (
  id: number,
  data: UpdateGatewayConversionSettingsParams,
): Promise<AxiosResponse<GatewayConversionSetting>> => {
  return axios
    .put(`gateway_conversion_settings/${id}`, data)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export type UpdateGatewayConversionSettingsNotifyParams = {
  notify: boolean;
};

type UpdateGatewayConversionSettingsNotifyResponse = {
  notify: boolean;
  id: string;
};

export const updateGatewayConversionSettingsNotify = async (
  id: number,
  data: UpdateGatewayConversionSettingsNotifyParams,
): Promise<AxiosResponse<UpdateGatewayConversionSettingsNotifyResponse>> => {
  return axios
    .patch(`gateway_conversion_settings/${id}/notify`, data, {
      headers: {
        'content-type': 'application/merge-patch+json',
      },
    })
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const deleteGatewayConversionSettings = async (id: number): Promise<AxiosResponse> => {
  return axios
    .delete(`gateway_conversion_settings/${id}`)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const getOrderConversionChartData = async (
  filter: OrderConversionChartRequestParams,
): Promise<OrderConversionChartData> => {
  const params = qs.stringify(
    {
      ...filter,
    },
    { arrayFormat: 'indices', indices: false, encode: true },
  );
  return axios
    .get(`monitoring/order_conversion_chart_data?${params}`)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};
